import { formatDisplayDateTime } from "./displayFormat.mjs";
const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function toNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
}

export function formatNumber(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) {
    return 0;
  }
  const normalized = Number(num.toFixed(1).replace(/\.0$/, ''));
  return Number.isNaN(normalized) ? 0 : normalized;
}

export function formatAsIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toDateObject(value) {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const withTimeMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]/);
    if (withTimeMatch) {
      const parsedWithTime = new Date(trimmed);
      if (!Number.isNaN(parsedWithTime.getTime())) {
        return new Date(
          parsedWithTime.getFullYear(),
          parsedWithTime.getMonth(),
          parsedWithTime.getDate(),
        );
      }
    }

    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }

  return null;
}

export function normalizeDateString(value) {
  if (!value) return '';

  if (value instanceof Date) {
    return formatAsIsoDate(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const directMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (directMatch) {
      return directMatch[1];
    }

    const parsed = new Date(trimmed);
    return formatAsIsoDate(parsed);
  }

  return '';
}

export function formatDate(dateString) {
  if (!dateString) return '';

  const date = toDateObject(dateString);
  if (date) {
    return formatAsIsoDate(date);
  }

  if (typeof dateString === 'string') {
    const [datePart] = dateString.split('T');
    return datePart;
  }

  return '';
}

// Afișarea `dd.mm.yyyy` și sumele `10 000,00` sunt în formatter-ul comun; re-exportate pentru apelanții existenți.
export { formatDisplayAmount, formatDisplayDate } from './displayFormat.mjs';

export function formatDateTime(dateTime) {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return dateTime;
  return formatDisplayDateTime(date);
}

const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const daysInYear = (year) => (isLeapYear(year) ? 366 : 365);

const toUtcMidnight = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const diffDaysUtc = (startUtc, endUtc) => Math.floor((endUtc - startUtc) / MS_PER_DAY) + 1;

export function computeAccruedUntilYearEnd(
  annual,
  carryover,
  accrualStartRaw,
  accrualFinishRaw = null,
  currentYearOverride = null
) {
  const annualDays = toNumber(annual, 0);
  const carry = toNumber(carryover, 0);
  if (!accrualStartRaw) return Number(carry.toFixed(2));

  const startDate = new Date(accrualStartRaw);
  if (Number.isNaN(startDate.getTime())) {
    return Number(carry.toFixed(2));
  }

  const currentYear = currentYearOverride ?? new Date().getUTCFullYear();
  const startYear = startDate.getUTCFullYear();
  const finishUtc = accrualFinishRaw ? toUtcMidnight(new Date(accrualFinishRaw)) : null;

  const startUtc = toUtcMidnight(startDate);

  let total = carry;

  if (finishUtc && finishUtc < startUtc) {
    return Number(carry.toFixed(2));
  }

  if (startYear > currentYear) {
    return Number(carry.toFixed(2));
  }

  if (startYear === currentYear) {
    const startOfYearUtc = Date.UTC(currentYear, 0, 1);
    const endOfYearUtc = Date.UTC(currentYear, 11, 31);
    const effectiveStartUtc = Math.max(startUtc, startOfYearUtc);
    const effectiveEndUtc = finishUtc ? Math.min(endOfYearUtc, finishUtc) : endOfYearUtc;
    if (effectiveEndUtc < effectiveStartUtc) return Number(carry.toFixed(2));
    const totalDaysYear = diffDaysUtc(startOfYearUtc, endOfYearUtc);
    const daysRemaining = diffDaysUtc(effectiveStartUtc, effectiveEndUtc);
    const prorated = daysRemaining > 0 ? annualDays * (daysRemaining / totalDaysYear) : 0;
    return Number((total + prorated).toFixed(2));
  }

  const endOfFirstYearUtc = finishUtc && finishUtc < Date.UTC(startYear, 11, 31) ? finishUtc : Date.UTC(startYear, 11, 31);
  const firstYearDays = diffDaysUtc(startUtc, endOfFirstYearUtc);
  const proratedFirstYear = firstYearDays > 0 ? annualDays * (firstYearDays / daysInYear(startYear)) : 0;
  total += proratedFirstYear;

  if (finishUtc) {
    const finishYear = new Date(finishUtc).getUTCFullYear();
    for (let year = startYear + 1; year < Math.min(currentYear, finishYear); year += 1) {
      total += annualDays;
    }
    if (finishYear <= currentYear) {
      const startOfFinishYear = Date.UTC(finishYear, 0, 1);
      const effectiveEnd = finishUtc;
      const daysFinishYear = diffDaysUtc(startOfFinishYear, Date.UTC(finishYear, 11, 31));
      const proratedFinish =
        effectiveEnd >= startOfFinishYear
          ? annualDays * (diffDaysUtc(startOfFinishYear, effectiveEnd) / daysFinishYear)
          : 0;
      total += Math.max(0, proratedFinish);
      return Number(total.toFixed(2));
    }
  } else {
    for (let year = startYear + 1; year < currentYear; year += 1) {
      total += annualDays;
    }
  }

  total += annualDays;

  return Number(total.toFixed(2));
}
